<template>
  <div class="cliente-creditos">
    <FilterFields
      :fields="[
        {
          type: 'text',
          label: 'Cliente',
          field: 'cliente',
        },
        {
          type: 'text',
          label: 'CPF/CNPJ',
          field: 'cnpj_cpf',
        },
        {
          type: 'select',
          label: 'Saldo',
          field: 'saldo',
          options: ['Com saldo', 'Sem saldo'],
        },
      ]"
    />

    <div class="d-flex justify-content-end mb-4">
      <button class="btn btn-primary font-weight-bold" @click="novoAjuste">
        Ajustar crédito
      </button>
    </div>

    <TableApp
      titulo="Crédito de Clientes"
      api-url="/financeiro/cliente-creditos"
      :fields="fields"
      classFilterColumn="clienteCreditos"
      ref="clienteCreditos"
      :perPage="10"
    >
      <template slot="customRow" scope="props">
        <span slot="cliente" v-if="props.field == 'cliente'">
          <div class="font-weight-bolder text-dark-75">
            {{ props.rowData.pessoa ? props.rowData.pessoa.nome : "-" }}
          </div>
          <div class="text-muted">
            {{ props.rowData.pessoa ? formatCpfCnpj(props.rowData.pessoa.cnpj_cpf) : "" }}
          </div>
        </span>

        <span slot="saldo" v-if="props.field == 'saldo'">
          <span class="label label-lg label-inline label-light-primary">
            R$ {{ numberToReal(props.rowData.saldo, 2) }}
          </span>
        </span>

        <span slot="updated_at" v-if="props.field == 'updated_at'">
          <span class="font-weight-bold text-muted">
            {{ formatDate(props.rowData.updated_at) }}
          </span>
        </span>

        <span slot="actions" v-if="props.field == 'actions'">
          <div class="row-actions" style="text-align: right !important">
            <button
              class="btn btn-sm btn-info btn-hover-info btn-icon mr-2"
              v-b-tooltip="'Extrato'"
              @click="abrirExtrato(props.rowData)"
            >
              <Eye :size="16" />
            </button>
            <button
              class="btn btn-sm btn-primary btn-hover-primary btn-icon mr-2"
              v-b-tooltip="'Editar saldo'"
              @click="abrirAjuste(props.rowData)"
            >
              <Pencil :size="16" />
            </button>
          </div>
        </span>
      </template>
    </TableApp>

    <b-modal id="modal-ajuste-credito" title="Ajustar crédito do cliente" centered>
      <b-form-group label="Cliente" label-for="cliente">
        <VueSelect
          ref="clienteCredito"
          :filterable="false"
          label="nome"
          :options="clientes"
          v-model="clienteSelecionado"
          @search="onSearchCliente"
          @input="setCliente"
        >
          <template slot="no-options"> Informe o nome do cliente </template>
          <template slot="option" slot-scope="option">
            <div class="d-center">{{ option.codigo }} - {{ option.nome }}</div>
          </template>
          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">{{ option.nome }}</div>
          </template>
        </VueSelect>
      </b-form-group>

      <b-row>
        <b-col>
          <b-form-group label="Saldo atual" label-for="saldoAtual">
            <b-form-input :value="'R$ ' + numberToReal(saldoAtual, 2)" disabled />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group label="Novo saldo" label-for="novoSaldo">
            <b-form-input
              id="novoSaldo"
              v-model.number="ajuste.saldo"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-alert v-if="valorAumentoAjuste > 0" show variant="info">
        Será gerado um novo voucher de R$
        {{ numberToReal(valorAumentoAjuste, 2) }}.
      </b-alert>

      <div v-if="valorReducaoAjuste > 0">
        <b-alert show variant="warning">
          Selecione o voucher que será consumido pelo ajuste.
        </b-alert>

        <b-form-group label="Voucher para baixa" label-for="voucherAjuste">
          <VueSelect
            label="codigo_voucher"
            :options="vouchersSuficientesAjuste"
            v-model="ajusteVoucherSelecionado"
          >
            <template slot="no-options">
              Nenhum voucher com saldo suficiente para este ajuste.
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-center">
                {{ option.codigo_voucher }} - R$
                {{ numberToReal(option.saldo, 2) }}
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center">
                {{ option.codigo_voucher }} - R$
                {{ numberToReal(option.saldo, 2) }}
              </div>
            </template>
          </VueSelect>
        </b-form-group>

        <b-alert
          v-if="valorTrocoAjuste > 0 && ajusteVoucherSelecionado"
          show
          variant="info"
        >
          Este ajuste vai gerar um novo voucher de troco de R$
          {{ numberToReal(valorTrocoAjuste, 2) }}.
        </b-alert>
      </div>

      <b-form-group label="Motivo do ajuste" label-for="descricao">
        <b-form-textarea
          id="descricao"
          v-model="ajuste.descricao"
          rows="3"
          placeholder="Ex.: acerto manual, conferência de voucher, ajuste administrativo"
        />
      </b-form-group>

      <template #modal-footer="{ cancel }">
        <b-button @click="cancel()">Cancelar</b-button>
        <b-button variant="primary" ref="salvar-ajuste-credito" @click="salvarAjuste">
          Salvar ajuste
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-extrato-credito"
      title="Extrato de crédito"
      size="xl"
      centered
      ok-only
      ok-title="Fechar"
    >
      <div class="mb-4" v-if="extratoCliente">
        <div class="font-weight-bolder text-dark-75">{{ extratoCliente.nome }}</div>
        <div class="text-muted">{{ formatCpfCnpj(extratoCliente.cnpj_cpf) }}</div>
      </div>

      <div class="extrato-saldo-card mb-5">
        <div>
          <div class="extrato-saldo-label">Saldo atual do cliente</div>
          <div class="extrato-saldo-valor">
            R$ {{ numberToReal(extratoSaldoCliente, 2) }}
          </div>
          <div class="extrato-saldo-info">
            Valor disponível para uso em vendas e abatimentos.
          </div>
        </div>
      </div>

      <b-table
        :items="extrato"
        :fields="extratoFields"
        small
        responsive
        bordered
        show-empty
        empty-text="Nenhum movimento encontrado."
      >
        <template #cell(created_at)="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template #cell(tipo)="{ item }">
          <span
            :class="
              item.tipo == 'E'
                ? 'label label-inline label-light-success'
                : 'label label-inline label-light-danger'
            "
          >
            {{ item.tipo == "E" ? "Entrada" : "Saída" }}
          </span>
        </template>
        <template #cell(valor)="{ item }">
          R$ {{ numberToReal(item.valor, 2) }}
        </template>
        <template #cell(status_voucher)="{ item }">
          <span :class="classeStatusVoucher(item)">
            {{ statusVoucher(item) }}
          </span>
        </template>
        <template #cell(codigo_voucher)="{ item }">
          {{ codigoVoucherMovimento(item) }}
        </template>
        <template #cell(actions)="{ item }">
          <button
            v-if="statusVoucher(item) === 'Ativo'"
            class="btn btn-sm btn-info btn-hover-info btn-icon"
            v-b-tooltip="{ title: 'Imprimir voucher', boundary: 'window' }"
            @click="imprimirVoucherAtivo(item)"
          >
            <PrintIcon :size="16" />
          </button>
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import TableApp from "@/components/Table/Table2";
import FilterFields from "@/components/FilterFields";
import VueSelect from "vue-select";
import Eye from "vue-material-design-icons/Eye.vue";
import Pencil from "vue-material-design-icons/Pencil.vue";
import PrintIcon from "vue-material-design-icons/Printer.vue";
import { SistemaMixin } from "@/modules/Application";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

export default {
  name: "app-cliente-creditos",
  mixins: [SistemaMixin],
  components: {
    TableApp,
    FilterFields,
    VueSelect,
    Eye,
    Pencil,
    PrintIcon,
  },
  data() {
    return {
      fields: [
        {
          label: "Cliente",
          field: "cliente",
          slot: true,
          sortable: false,
          width: "40%",
        },
        {
          label: "Saldo",
          field: "saldo",
          slot: true,
          sortable: true,
          width: "20%",
        },
        {
          label: "Atualizado em",
          field: "updated_at",
          slot: true,
          sortable: false,
          width: "20%",
        },
        {
          label: "Ações",
          field: "actions",
          slot: true,
          sortable: false,
          thClass: "text-right",
          tdClass: "text-right",
          width: "20%",
        },
      ],
      extratoFields: [
        { key: "created_at", label: "Data" },
        { key: "tipo", label: "Tipo" },
        { key: "origem", label: "Origem" },
        { key: "valor", label: "Valor" },
        { key: "descricao", label: "Descrição" },
        { key: "codigo_voucher", label: "Voucher" },
        { key: "status_voucher", label: "Status" },
        { key: "actions", label: "Ações" },
      ],
      clientes: [],
      clienteSelecionado: null,
      saldoAtual: 0,
      ajuste: {
        saldo: 0,
        descricao: "",
      },
      extrato: [],
      extratoCliente: null,
      extratoSaldoCliente: 0,
      extratoVouchersDisponiveis: [],
      ajusteVouchersDisponiveis: [],
      ajusteVoucherSelecionado: null,
      clienteSearchTimer: null,
    };
  },
  computed: {
    valorAumentoAjuste() {
      const novoSaldo = Number(this.ajuste.saldo) || 0;
      const saldoAnterior = Number(this.saldoAtual) || 0;

      return this.roundTo2Digits(Math.max(novoSaldo - saldoAnterior, 0));
    },
    valorReducaoAjuste() {
      const novoSaldo = Number(this.ajuste.saldo) || 0;
      const saldoAnterior = Number(this.saldoAtual) || 0;

      return this.roundTo2Digits(Math.max(saldoAnterior - novoSaldo, 0));
    },
    valorTrocoAjuste() {
      const saldoVoucher = Number(this.ajusteVoucherSelecionado?.saldo) || 0;

      return this.roundTo2Digits(Math.max(saldoVoucher - this.valorReducaoAjuste, 0));
    },
    vouchersSuficientesAjuste() {
      return this.ajusteVouchersDisponiveis.filter((voucher) => {
        return Number(voucher.saldo) >= this.valorReducaoAjuste;
      });
    },
  },
  methods: {
    roundTo2Digits(value, decPlaces = 2) {
      return Math.round(value * Math.pow(10, decPlaces)) / Math.pow(10, decPlaces);
    },
    novoAjuste() {
      this.clienteSelecionado = null;
      this.saldoAtual = 0;
      this.ajuste = {
        saldo: 0,
        descricao: "",
      };
      this.ajusteVouchersDisponiveis = [];
      this.ajusteVoucherSelecionado = null;
      this.$bvModal.show("modal-ajuste-credito");
    },
    async abrirAjuste(row) {
      this.clienteSelecionado = row.pessoa;
      this.saldoAtual = Number(row.saldo) || 0;
      this.ajuste = {
        saldo: Number(row.saldo) || 0,
        descricao: "",
      };
      this.ajusteVoucherSelecionado = null;
      await this.carregarVouchersAjuste(row.id_pessoa);
      this.$bvModal.show("modal-ajuste-credito");
    },
    async setCliente(cliente) {
      if (!cliente) return;
      this.clienteSelecionado = cliente;
      const [saldoResponse, vouchersResponse] = await Promise.all([
        this.$http.post("/financeiro/cliente-creditos/saldo", {
          id_pessoa: cliente.id_pessoa,
        }),
        this.$http.post("/financeiro/cliente-creditos/vouchers-disponiveis", {
          id_pessoa: cliente.id_pessoa,
        }),
      ]);
      this.saldoAtual = Number(saldoResponse.data.saldo) || 0;
      this.ajuste.saldo = this.saldoAtual;
      this.ajusteVouchersDisponiveis = vouchersResponse.data ?? [];
      this.ajusteVoucherSelecionado = null;
    },
    async carregarVouchersAjuste(idPessoa) {
      if (!idPessoa) {
        this.ajusteVouchersDisponiveis = [];
        return;
      }

      const { data } = await this.$http.post(
        "/financeiro/cliente-creditos/vouchers-disponiveis",
        {
          id_pessoa: idPessoa,
        }
      );

      this.ajusteVouchersDisponiveis = data ?? [];
    },
    onSearchCliente(search, loading) {
      loading(true);
      if (this.clienteSearchTimer) clearTimeout(this.clienteSearchTimer);
      this.clienteSearchTimer = setTimeout(async () => {
        if (!search || search.length < 2) {
          this.clientes = [];
          loading(false);
          return;
        }
        this.clientes = await this.getClientes(search);
        loading(false);
      }, 350);
    },
    salvarAjuste() {
      if (!this.clienteSelecionado?.id_pessoa) {
        this.$toast.warning("Selecione o cliente.");
        return;
      }

      if (Number(this.ajuste.saldo) < 0) {
        this.$toast.warning("O saldo não pode ser negativo.");
        return;
      }

      if (this.valorReducaoAjuste > 0) {
        if (!this.ajusteVoucherSelecionado?.id) {
          this.$toast.warning("Selecione o voucher que será consumido.");
          return;
        }

        if (Number(this.ajusteVoucherSelecionado.saldo) < this.valorReducaoAjuste) {
          this.$toast.warning("O voucher selecionado não cobre este ajuste.");
          return;
        }
      }

      this.getRequestAdmin(async () => {
        await this.executarAjuste();
      });
    },
    async executarAjuste() {
      this.addLoadingToButton("salvar-ajuste-credito");
      try {
        const novoSaldo = Number(this.ajuste.saldo) || 0;
        const saldoAnterior = Number(this.saldoAtual) || 0;
        const valorVoucher = this.roundTo2Digits(
          Math.max(novoSaldo - saldoAnterior, 0)
        );

        const { data } = await this.$http.post(
          "/financeiro/cliente-creditos/ajustar-saldo",
          {
            id_pessoa: this.clienteSelecionado.id_pessoa,
            saldo: novoSaldo,
            descricao: this.ajuste.descricao,
            saldo_anterior: saldoAnterior,
            gerar_voucher: valorVoucher > 0,
            valor_voucher: valorVoucher,
            id_movimento_voucher: this.ajusteVoucherSelecionado?.id,
            codigo_voucher: this.ajusteVoucherSelecionado?.codigo_voucher,
            origem: "ajuste_credito",
          }
        );
        this.$toast.success("Crédito ajustado com sucesso.");
        this.imprimirVoucherAjuste(data?.["voucher-url"] || data?.voucher_url);
        this.$bvModal.hide("modal-ajuste-credito");
        this.$refs.clienteCreditos.refresh();
      } catch (error) {
        this.$toast.error(error?.response?.data?.msg ?? "Erro ao ajustar crédito.");
      }
      this.removeLoadingToButton("salvar-ajuste-credito");
    },
    imprimirVoucherAjuste(url) {
      if (!url) return;

      const printVoucher = window.open(url, "_blank");
      if (!printVoucher || printVoucher.closed) {
        this.$swal.fire({
          title: "Voucher gerado",
          text: "A impressão automática foi bloqueada pelo navegador.",
          icon: "info",
          confirmButtonText: "Abrir voucher",
          confirmButtonColor: "var(--primary-color)",
        }).then(() => {
          window.open(url, "_blank");
        });
        return;
      }

      printVoucher.focus();
      setTimeout(() => {
        printVoucher.print();
      }, 1000);
    },
    async abrirExtrato(row) {
      this.extratoCliente = row.pessoa;
      this.extratoSaldoCliente = Number(row.saldo) || 0;
      const idPessoa = row.id_pessoa;
      const [movimentosResponse, vouchersResponse] = await Promise.all([
        this.$http.post("/financeiro/cliente-creditos/movimentos", {
          id_pessoa: idPessoa,
          per_page: 50,
        }),
        this.$http.post("/financeiro/cliente-creditos/vouchers-disponiveis", {
          id_pessoa: idPessoa,
        }),
      ]);

      this.extrato = movimentosResponse.data.data ?? [];
      this.extratoVouchersDisponiveis = vouchersResponse.data ?? [];
      this.$bvModal.show("modal-extrato-credito");
    },
    codigoVoucherMovimento(item) {
      return (
        item.codigo_voucher ||
        item.dados?.codigo_voucher ||
        item.dados?.codigo_voucher_origem ||
        ""
      );
    },
    voucherDisponivelMovimento(item) {
      const codigoVoucher = this.codigoVoucherMovimento(item);

      return this.extratoVouchersDisponiveis.find((voucher) => {
        return (
          (item.id && Number(voucher.id) === Number(item.id)) ||
          (codigoVoucher && voucher.codigo_voucher === codigoVoucher)
        );
      });
    },
    statusVoucher(item) {
      const codigoVoucher = this.codigoVoucherMovimento(item);

      if (!codigoVoucher) return "-";

      if (item.tipo === "S") return "Uso";

      const voucherDisponivel = this.voucherDisponivelMovimento(item);

      if (!voucherDisponivel) return "Baixado";

      const saldoDisponivel = Number(voucherDisponivel.saldo) || 0;
      const valorOriginal = Number(item.valor) || 0;

      if (saldoDisponivel < valorOriginal) {
        return `Parcial: R$ ${this.numberToReal(saldoDisponivel, 2)}`;
      }

      return "Ativo";
    },
    classeStatusVoucher(item) {
      const status = this.statusVoucher(item);

      if (status === "Ativo") return "label label-inline label-light-success";
      if (status.includes("Parcial")) return "label label-inline label-light-warning";
      if (status === "Baixado") return "label label-inline label-light-danger";
      if (status === "Uso") return "label label-inline label-light-info";

      return "text-muted";
    },
    async imprimirVoucherAtivo(item) {
      try {
        const { data } = await this.$http.post(
          "/financeiro/cliente-creditos/imprimir-voucher",
          {
            id_movimento: item.id,
            codigo_voucher: this.codigoVoucherMovimento(item),
          }
        );
        const voucherUrl = data?.["voucher-url"] || data?.voucher_url;

        if (!voucherUrl) {
          this.$toast.warning("Não foi possível gerar o voucher.");
          return;
        }

        this.imprimirVoucherAjuste(voucherUrl);
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ?? "Erro ao imprimir voucher."
        );
      }
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: "Crédito de Clientes",
      },
    ]);
  },
};
</script>

<style scoped>
.cliente-creditos .btn-primary,
.cliente-creditos .label-light-primary {
  border-color: var(--primary-color) !important;
}

.extrato-saldo-card {
  align-items: center;
  background: linear-gradient(135deg, rgba(28, 196, 184, 0.12), rgba(255, 167, 0, 0.12));
  border: 1px solid rgba(28, 196, 184, 0.22);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  padding: 18px 22px;
}

.extrato-saldo-label {
  color: #7e8299;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

.extrato-saldo-valor {
  color: #1f2437;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.2;
  margin-top: 4px;
}

.extrato-saldo-info {
  color: #5e6278;
  font-size: 0.95rem;
  margin-top: 4px;
}

</style>
